import { FC, useMemo } from 'react';
import TileGridContainer from 'features/tiles/tileGridContainer';
import RevenueSearchInteractionBar from 'features/revenue/revenueSearchBar';
import TileWithoutDefaultStyling from 'features/tiles/tileWithoutDefaultStyling';
import { PaginatedTable } from 'features/pagination/paginatedTable';
import { usePaginatedQuery } from 'features/pagination/hooks/usePaginatedQuery';
import { useGetPaginatedRevenueQuery, usePrefetch } from 'app/apis/queryApi';
import { selectCurrencyCode } from 'features/settings/settingsSlice';
import { useSelector } from 'react-redux';
import { RootState } from 'app/store';
import { selectViewSelectionByComponentId } from 'features/data-view-selection/dataViewSelectionSlice';
import { ViewType } from 'features/data-view-selection/view-type';
import { GetRevenueRequest } from 'features/revenue/types/get-revenue-request';
import { debugToggleByType } from 'features/internal/internal-settings.slice';
import { ToggleType } from 'features/internal/internal.models';
import revenueSimpleColumnDefs from 'features/revenue/table/revenue-simple-column-defs';
import revenueDetailedColumnDefs from 'features/revenue/table/revenue-detailed-column-defs';
import { selectDoIHavePendingDeletesForComponent } from 'features/row-deletion/rowPendingDeletionSlice';
import Alert from 'common/components/alerts/alert';
import { GetRevenueResponse } from 'features/revenue/types/get-revenue-response';

const Revenue: FC = () => {
  const revenueComponentId = 'revenue-component';
  const selectedCurrencyCode = useSelector(selectCurrencyCode);

  const revenueIdToggle = useSelector((state: RootState) =>
    debugToggleByType(state, ToggleType.DisplayRevenueId)
  );

  const { selection: currentViewType } = useSelector((state: RootState) =>
    selectViewSelectionByComponentId(state, revenueComponentId)
  );

  const hasPendingDeletes = useSelector((state: RootState) =>
    selectDoIHavePendingDeletesForComponent(state, revenueComponentId)
  );

  const prefetchPage = usePrefetch('getPaginatedRevenue');
  const { data, isLoading, isFetching } = usePaginatedQuery<
    GetRevenueRequest,
    GetRevenueResponse
  >(
    useGetPaginatedRevenueQuery,
    prefetchPage,
    revenueComponentId,
    {
      currencyCode: selectedCurrencyCode
    },
    {
      initialColumnName: 'revenuecloseddatetime',
      initialOrderDirection: 'ASC'
    }
  );

  const columnDefs = useMemo(() => {
    return currentViewType === ViewType.Simple
      ? revenueSimpleColumnDefs(
          revenueComponentId,
          revenueIdToggle?.enabled ?? false,
          selectedCurrencyCode
        )
      : revenueDetailedColumnDefs(
          revenueComponentId,
          data?.data ?? [],
          selectedCurrencyCode,
          revenueIdToggle?.enabled ?? false
        );
  }, [revenueIdToggle, selectedCurrencyCode, currentViewType, data]);

  if (!isLoading && !isFetching && !data) {
    return <></>;
  }

  return (
    <TileGridContainer>
      <>
        <RevenueSearchInteractionBar
          componentId={revenueComponentId}
          currentViewType={currentViewType}
        />
        {hasPendingDeletes && (
          <Alert
            type="info"
            title="Revenue Deletion In Progress"
            message="Please note revenue deletion is in progress. Records pending delete are marked below in red. This process may take a few minutes to complete."
          />
        )}

        <TileWithoutDefaultStyling className="min-h-fit justify-center align-middle bg-white border rounded border-off-navy w-full my-2 grow basis-full">
          <PaginatedTable
            columns={columnDefs}
            data={data?.data ?? []}
            isLoading={isLoading}
            isFetching={isFetching}
            componentId={revenueComponentId}
            getRowId={(row, relativeIndex, parent) => {
              return row.revenueEntity ? row.revenueEntity.id : row.id;
            }}
            getRowData={() => null}
          />
        </TileWithoutDefaultStyling>
      </>
    </TileGridContainer>
  );
};

export default Revenue;
